export interface IcdRow {
  kode: string;
  nama: string;
}

export interface SoapSumber {
  s: string | null;
  o: string | null;
  examined_at: string | null;
}

export interface RujukanMasuk {
  dokterId: number | null;
  dokterNama: string | null;
  tanggal: string | null;
}

export type JenisPenunjang = "lab" | "radiologi" | "lain";

export interface KandidatPenunjang {
  key: string;
  jenis: JenisPenunjang;
  tanggal: string;
  nama: string;
  unit: string;
  judul: string;
  ringkas: string;
  teks: string;
}

function sameRegister(nilai: unknown, registerId: unknown): boolean {
  if (nilai === null || nilai === undefined || nilai === "") return false;

  return String(nilai) === String(registerId);
}

function stamp(nilai: unknown): number {
  const teks = String(nilai == null ? "" : nilai).trim();
  if (!teks) return 0;

  const waktu = new Date(teks.replace(" ", "T")).getTime();

  return isNaN(waktu) ? 0 : waktu;
}

function tanggalSaja(nilai: unknown): string | null {
  const teks = String(nilai == null ? "" : nilai).trim();
  if (!teks) return null;

  const cocok = teks.match(/^(\d{4}-\d{2}-\d{2})/);

  return cocok ? cocok[1] : null;
}

function teksBersih(nilai: unknown): string {
  return String(nilai == null ? "" : nilai).trim();
}

export function pickSoapForRegister(
  rows: Array<any>,
  registerId: string | number,
  userId?: string | number
): SoapSumber | null {
  const milikRegister = (rows || []).filter(row =>
    sameRegister(row && row.register_id, registerId)
  );

  if (!milikRegister.length) return null;

  const terbaruDulu = milikRegister
    .slice()
    .sort((a, b) => stamp(b.examined_at) - stamp(a.examined_at));

  const milikSaya =
    userId === null || userId === undefined
      ? []
      : terbaruDulu.filter(
          row => row.user && String(row.user.id) === String(userId)
        );

  const dipakai = milikSaya.length ? milikSaya[0] : terbaruDulu[0];

  return {
    s: teksBersih(dipakai.s) || null,
    o: teksBersih(dipakai.o) || null,
    examined_at: teksBersih(dipakai.examined_at) || null
  };
}

function toIcdRows(items: Array<any>): IcdRow[] {
  return (items || [])
    .map(item => ({
      kode: teksBersih(item && item.kode),
      nama: teksBersih(item && item.nama)
    }))
    .filter(row => row.kode && row.nama);
}

export function mergeIcdRows(
  existing: Array<IcdRow>,
  incoming: Array<IcdRow>
): IcdRow[] {
  const hasil: IcdRow[] = [];
  const terpakai: Record<string, boolean> = {};

  [...(existing || []), ...(incoming || [])].forEach(row => {
    const kode = teksBersih(row && row.kode);
    const nama = teksBersih(row && row.nama);
    if (!kode || !nama) return;
    if (terpakai[kode]) return;

    terpakai[kode] = true;
    hasil.push({ kode, nama });
  });

  return hasil;
}

export function mapResumeToIcd(
  rows: Array<any>,
  registerId: string | number
): { diagnosis: IcdRow[]; prosedur: IcdRow[] } {
  const milikRegister = (rows || []).filter(
    row =>
      sameRegister(row && row.register, registerId) ||
      sameRegister(row && row.register_id, registerId)
  );

  const diagnosis: IcdRow[] = [];
  const prosedur: IcdRow[] = [];

  milikRegister.forEach(row => {
    diagnosis.push(...toIcdRows(row.diagnosis));
    prosedur.push(...toIcdRows(row.prosedur));
  });

  return {
    diagnosis: mergeIcdRows([], diagnosis),
    prosedur: mergeIcdRows([], prosedur)
  };
}

export function pickRujukanMasuk(
  rows: Array<any>,
  unitGroupId?: string | number
): RujukanMasuk | null {
  const semua = (rows || []).filter(row => row && row.dokter_pengirim);
  if (!semua.length) return null;

  const seUnit =
    unitGroupId === null || unitGroupId === undefined || unitGroupId === ""
      ? []
      : semua.filter(
          row =>
            row.unit_tujuan &&
            String(row.unit_tujuan.id) === String(unitGroupId)
        );

  const kandidat = seUnit.length ? seUnit : semua;

  const terbaru = kandidat
    .slice()
    .sort((a, b) => stamp(b.created_at) - stamp(a.created_at))[0];

  const dokterId = parseInt(String(terbaru.dokter_pengirim.id), 10);

  return {
    dokterId: isNaN(dokterId) ? null : dokterId,
    dokterNama: teksBersih(terbaru.dokter_pengirim.name) || null,
    tanggal: tanggalSaja(terbaru.created_at)
  };
}

function formatTanggal(nilai: unknown): string {
  const tanggal = tanggalSaja(nilai);
  if (!tanggal) return "";

  const bagian = tanggal.split("-");

  return `${bagian[2]}-${bagian[1]}-${bagian[0]}`;
}

function barisLab(item: any): string {
  const nama = teksBersih(item && item.nama_test);
  const hasil = teksBersih(item && item.hasil);
  if (!nama || !hasil) return "";

  const satuan = teksBersih(item && item.satuan);
  const flag = teksBersih(item && item.flag).toUpperCase();
  const tanda = flag === "H" || flag === "L" ? ` (${flag})` : "";

  return `${nama} ${hasil}${satuan ? " " + satuan : ""}${tanda}`;
}

function judulLab(order: any, tanggal: string): string {
  return `Lab${tanggal ? " " + tanggal : ""}${
    order && order.lis_sampel ? " · Sampel " + teksBersih(order.lis_sampel) : ""
  }`;
}

export function buildKandidatPenunjang(
  labOrders: Array<any>,
  radiologiOrders: Array<any>
): KandidatPenunjang[] {
  const hasil: KandidatPenunjang[] = [];

  (labOrders || []).forEach((order, i) => {
    const items = (Array.isArray(order.hasil) ? order.hasil : [])
      .map(barisLab)
      .filter(baris => !!baris);

    if (!items.length) return;

    const tanggal = formatTanggal(order.acc_date || order.tgl_order);
    const judul = judulLab(order, tanggal);

    hasil.push({
      key: `lab-${teksBersih(order.lis_sampel) || teksBersih(order.no_order) || i}`,
      jenis: "lab",
      tanggal,
      nama: order.lis_sampel
        ? "Sampel " + teksBersih(order.lis_sampel)
        : "Laboratorium",
      unit: teksBersih(order.unit && order.unit.nama),
      judul,
      ringkas: items.join("; "),
      teks: `${judul}: ${items.join("; ")}`
    });
  });

  (radiologiOrders || []).forEach((order, i) => {
    const tanggal = formatTanggal(order.tgl);

    (Array.isArray(order.hasil) ? order.hasil : []).forEach((item, j) => {
      const nama = teksBersih(item && item.nama);
      const bacaan = teksBersih(item && item.description);
      if (!nama && !bacaan) return;

      const judul = `Radiologi${tanggal ? " " + tanggal : ""}${
        nama ? " · " + nama : ""
      }`;

      hasil.push({
        key: `ro-${teksBersih(order.no_transaksi) || i}-${teksBersih(
          item && item.acc_number
        ) || j}`,
        jenis: "radiologi",
        tanggal: tanggal || formatTanggal(item && item.reportDate),
        nama: nama || "Radiologi",
        unit: teksBersih(order.unit && order.unit.nama),
        judul,
        ringkas: bacaan || nama,
        teks: bacaan ? `${judul}: ${bacaan}` : judul
      });
    });
  });

  return hasil;
}

export function buildKandidatPenunjangLain(
  rows: Array<any>
): KandidatPenunjang[] {
  return (rows || [])
    .map((row, i) => {
      const kategori = teksBersih(
        row && row.penunjang_lain_kategori && row.penunjang_lain_kategori.nama
      );
      const tanggal = formatTanggal(row && (row.tanggal || row.waktu_periksa));
      const catatan = teksBersih(row && row.catatan);
      const metode = teksBersih(row && row.kb_metode_nama);

      const judul = [kategori || "Penunjang Lain", tanggal]
        .filter(teks => !!teks)
        .join(" ");
      const isi = catatan || metode;

      return {
        key: `pl-${teksBersih(row && row.id) || i}`,
        jenis: "lain" as JenisPenunjang,
        tanggal,
        nama: kategori || "Penunjang Lain",
        unit: teksBersih(row && row.unit && row.unit.nama),
        judul,
        ringkas: isi,
        teks: isi ? `${judul}: ${isi}` : judul
      };
    })
    .filter(item => !!item.judul);
}

const PEMISAH = "\r\n";

export function formatPenunjang(selected: Array<KandidatPenunjang>): string {
  return (selected || [])
    .map(item => teksBersih(item && item.teks))
    .filter(teks => !!teks)
    .join(PEMISAH);
}

function rapikan(teks: string): string {
  return teks.replace(/^(?:\r?\n)+/, "").replace(/(?:\r?\n)+$/, "");
}

function hitungKemunculan(sumber: string, potongan: string): number {
  if (!potongan) return 0;

  let jumlah = 0;
  let mulai = 0;

  for (;;) {
    const posisi = sumber.indexOf(potongan, mulai);
    if (posisi === -1) return jumlah;

    jumlah += 1;
    mulai = posisi + potongan.length;
  }
}

function tempelTeks(sumber: string, potongan: string): string {
  const bersih = rapikan(sumber);

  return bersih ? bersih + PEMISAH + potongan : potongan;
}

function cabutTeks(sumber: string, potongan: string): string {
  const posisi = sumber.indexOf(potongan);
  if (posisi === -1) return sumber;

  const sebelum = rapikan(sumber.slice(0, posisi));
  const sesudah = rapikan(sumber.slice(posisi + potongan.length));

  if (!sebelum) return sesudah;
  if (!sesudah) return sebelum;

  return sebelum + PEMISAH + sesudah;
}

function teksUnik(semua: Array<KandidatPenunjang>): string[] {
  const kumpulan: string[] = [];

  (semua || []).forEach(item => {
    const teks = teksBersih(item && item.teks);
    if (!teks || kumpulan.indexOf(teks) !== -1) return;

    kumpulan.push(teks);
  });

  return kumpulan.sort((a, b) => b.length - a.length);
}

function hitungSemua(
  sumber: string,
  unik: Array<string>
): Record<string, number> {
  const jumlah: Record<string, number> = {};
  let sisa = sumber;

  unik.forEach(potongan => {
    jumlah[potongan] = hitungKemunculan(sisa, potongan);
    sisa = sisa.split(potongan).join("");
  });

  return jumlah;
}

export function pilihanDariTeks(
  sumber: string | null | undefined,
  semua: Array<KandidatPenunjang>
): KandidatPenunjang[] {
  const jumlah = hitungSemua(String(sumber == null ? "" : sumber), teksUnik(semua));
  const terpakai: Record<string, number> = {};

  return (semua || []).filter(item => {
    const teks = teksBersih(item && item.teks);
    if (!teks) return false;

    const dipakai = terpakai[teks] || 0;
    if (dipakai >= (jumlah[teks] || 0)) return false;

    terpakai[teks] = dipakai + 1;

    return true;
  });
}

export function selaraskanPenunjang(
  sumber: string | null | undefined,
  semua: Array<KandidatPenunjang>,
  terpilih: Array<KandidatPenunjang>
): string {
  const kunci = (terpilih || []).map(item => item && item.key);
  const unik = teksUnik(semua);

  const target: Record<string, number> = {};
  unik.forEach(potongan => {
    target[potongan] = 0;
  });

  (semua || []).forEach(item => {
    const teks = teksBersih(item && item.teks);
    if (!teks || kunci.indexOf(item.key) === -1) return;

    target[teks] = (target[teks] || 0) + 1;
  });

  let teks = String(sumber == null ? "" : sumber);
  const sekarang = hitungSemua(teks, unik);

  unik.forEach(potongan => {
    let ada = sekarang[potongan] || 0;
    const mau = target[potongan] || 0;

    while (ada > mau) {
      teks = cabutTeks(teks, potongan);
      ada -= 1;
    }

    while (ada < mau) {
      teks = tempelTeks(teks, potongan);
      ada += 1;
    }
  });

  return teks;
}

export interface ItemHasilLab {
  key: string;
  nama: string;
  hasil: string;
  satuan: string;
  rujukan: string;
  kritis: boolean;
  teks: string;
}

export interface SampelHasilLab {
  key: string;
  tanggal: string;
  unit: string;
  accBy: string;
  judul: string;
  items: ItemHasilLab[];
}

export function buildSampelLab(orders: Array<any>): SampelHasilLab[] {
  const hasil: SampelHasilLab[] = [];

  (orders || []).forEach((order, i) => {
    const kunci = String(i);

    const items: ItemHasilLab[] = [];

    (Array.isArray(order && order.hasil) ? order.hasil : []).forEach(
      (item, j) => {
        const teks = barisLab(item);
        if (!teks) return;

        const flag = teksBersih(item.flag).toUpperCase();

        items.push({
          key: `lab-${kunci}-${j}`,
          nama: teksBersih(item.nama_test),
          hasil: teksBersih(item.hasil),
          satuan: teksBersih(item.satuan),
          rujukan: teksBersih(item.nilai_normal),
          kritis: flag === "H" || flag === "L",
          teks
        });
      }
    );

    if (!items.length) return;

    hasil.push({
      key: `lab-${kunci}`,
      tanggal: teksBersih(order.acc_date || order.tgl_order),
      unit: teksBersih((order.unit && order.unit.nama) || order.nama_unit),
      accBy: teksBersih(order.acc_by),
      judul: judulLab(order, formatTanggal(order.acc_date || order.tgl_order)),
      items
    });
  });

  return hasil;
}

const PEMISAH_ITEM = "; ";

function barisTeks(sumber: string | null | undefined): string[] {
  const teks = String(sumber == null ? "" : sumber);

  return teks ? teks.split(/\r?\n/) : [];
}

function segmenBaris(baris: string, awalan: string): string[] | null {
  const bersih = baris.trim();
  if (bersih.indexOf(awalan) !== 0) return null;

  return bersih
    .slice(awalan.length)
    .split(PEMISAH_ITEM)
    .map(seg => seg.trim())
    .filter(seg => !!seg);
}

interface KelompokLab {
  awalan: string;
  items: ItemHasilLab[];
}

function kelompokLab(semua: Array<SampelHasilLab>): KelompokLab[] {
  const kelompok: KelompokLab[] = [];
  const posisi: Record<string, number> = {};

  (semua || []).forEach(sampel => {
    const awalan = sampel.judul + ": ";

    if (posisi[awalan] === undefined) {
      posisi[awalan] = kelompok.length;
      kelompok.push({ awalan, items: [] });
    }

    kelompok[posisi[awalan]].items.push(...sampel.items);
  });

  return kelompok;
}

export function pilihanLabDariTeks(
  sumber: string | null | undefined,
  semua: Array<SampelHasilLab>
): string[] {
  const baris = barisTeks(sumber);
  const kunci: string[] = [];

  kelompokLab(semua).forEach(({ awalan, items }) => {
    const segmen: string[] = [];

    baris.forEach(b => {
      const isi = segmenBaris(b, awalan);
      if (isi) segmen.push(...isi);
    });

    items.forEach(item => {
      const posisi = segmen.indexOf(item.teks);
      if (posisi === -1) return;

      segmen.splice(posisi, 1);
      kunci.push(item.key);
    });
  });

  return kunci;
}

export function selaraskanLab(
  sumber: string | null | undefined,
  semua: Array<SampelHasilLab>,
  terpilih: Array<string>
): string {
  const baris: Array<string | null> = barisTeks(sumber);

  kelompokLab(semua).forEach(({ awalan, items }) => {
    const teksItem = items.map(item => item.teks);
    const dipilih = items
      .filter(item => (terpilih || []).indexOf(item.key) !== -1)
      .map(item => item.teks);

    const posisi: number[] = [];
    const lain: string[] = [];

    baris.forEach((b, idx) => {
      if (b === null) return;

      const isi = segmenBaris(b, awalan);
      if (!isi) return;

      posisi.push(idx);
      isi.forEach(seg => {
        if (teksItem.indexOf(seg) === -1) lain.push(seg);
      });
    });

    const baru = dipilih.length
      ? awalan + dipilih.concat(lain).join(PEMISAH_ITEM)
      : null;

    if (posisi.length) {
      posisi.forEach((idx, urutan) => {
        baris[idx] = urutan === 0 ? baru : null;
      });

      return;
    }

    if (!baru) return;

    while (baris.length && !String(baris[baris.length - 1]).trim()) {
      baris.pop();
    }

    baris.push(baru);
  });

  return rapikan(
    baris.filter(b => b !== null).join(PEMISAH)
  );
}

export default {
  pickSoapForRegister,
  mapResumeToIcd,
  mergeIcdRows,
  pickRujukanMasuk,
  buildKandidatPenunjang,
  buildKandidatPenunjangLain,
  formatPenunjang,
  pilihanDariTeks,
  selaraskanPenunjang,
  buildSampelLab,
  pilihanLabDariTeks,
  selaraskanLab
};
